<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  hasRole,
  GLOBAL_ADMIN_ROLE,
  TIER_PRICING_ADMIN_ROLE,
  TIER_PRICING_CONTRIBUTOR_ROLE_V2,
  SPOT_EXCLUSION_CONTRIBUTOR_ROLE,
  SPOT_EXCLUSION_ADMIN_ROLE
} from "@/auth";

const route = useRoute();
const router = useRouter();

const canSeeTierPricing = computed(
  () =>
    hasRole(GLOBAL_ADMIN_ROLE) ||
    hasRole(TIER_PRICING_ADMIN_ROLE) ||
    hasRole(TIER_PRICING_CONTRIBUTOR_ROLE_V2)
);

const canSeeSpotExclusion = computed(
  () =>
    hasRole(GLOBAL_ADMIN_ROLE) ||
    hasRole(SPOT_EXCLUSION_ADMIN_ROLE) ||
    hasRole(SPOT_EXCLUSION_CONTRIBUTOR_ROLE)
);

const navItems = computed(() => {
  const items = [];
  if (canSeeTierPricing.value) {
    items.push({ label: "Tier Pricing", path: "/tier-pricing" });
  }
  if (canSeeSpotExclusion.value) {
    items.push({ label: "Spot Exclusion", path: "/spot-exclusion" });
  }

  return items;
});

const isActive = (path: string) => route.path === path;

const navigate = (path: string) => {
  router.push(path);
};
</script>

<template>
  <nav v-if="navItems.length > 0" class="app-nav">
    <div class="app-nav__inner">
      <button
        v-for="item in navItems"
        :key="item.path"
        class="app-nav__item"
        :class="{ 'app-nav__item--active': isActive(item.path) }"
        @click="navigate(item.path)"
      >
        {{ item.label }}
      </button>
    </div>
  </nav>
</template>

<style scoped lang="scss">
.app-nav {
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  z-index: 99;
  height: 44px;
  background: #fff;
  border-bottom: 1px solid var(--mds_brand_appearance_neutral_default_border-color);
  display: flex;
  align-items: stretch;

  &__inner {
    display: flex;
    align-items: stretch;
    padding: 0 24px;
    gap: 0;
  }

  &__item {
    display: flex;
    align-items: center;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 400;
    color: var(--mds_brand_appearance_neutral_weak_text-color);
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
    margin-bottom: -1px;

    &:hover {
      color: var(--mds_brand_appearance_neutral_default_text-color);
    }

    &--active {
      color: var(--mds_brand_appearance_neutral_default_text-color);
      font-weight: 600;
      border-bottom-color: var(--mds_brand_appearance_primary_default_background-color);
    }
  }
}
</style>
